import { createAsyncThunk, createSlice } from '@reduxjs/toolkit'
import MenuService from 'services/system/MenuService'
import * as Icons from '@ant-design/icons'
import { APP_PREFIX_PATH } from 'configs/AppConfig'

export const initialState = {
  loading: false,
  message: '',
  navigationConfig: [],
  bookmarkConfig: [],
  leftMenu: [],
}

const transformMenuData = data => {
  const iconMap = { ...Icons }

  // 재귀 함수로 submenu의 깊이에 상관없이 메뉴 객체를 생성합니다.
  const createMenu = id => {
    const menuItem = data.find(item => item.key === id)
    if (!menuItem) return null

    // 메뉴 아이템에서 필요한 속성만 추출합니다.
    const { key, path, title, titleLcCode, icon, submenuKey, menuStructuresYn, displayFlag, activeFlag } = menuItem

    const submenu = data.filter(item => item.submenuKey === key).map(item => createMenu(item.key))

    const isSubmenu = submenu.some(menu => menu !== null)
    if (menuStructuresYn === 'Y' && (submenu?.length === 0 || !isSubmenu)) return null

    // 새로운 메뉴 객체를 생성합니다.
    const newMenu = {
      key,
      path: `${APP_PREFIX_PATH}/${path}`,
      component: path,
      title,
      titleLcCode,
      submenu,
      showMenu: displayFlag === 'Y' && activeFlag === 'Y',
      breadcrumb: true,
    }

    newMenu.icon = iconMap[icon]

    // isGroupTitle을 ROOT 키에만 추가합니다.
    if (submenuKey === 'ROOT') {
      newMenu.isGroupTitle = true
    }

    return newMenu
  }

  // 최상위 메뉴 아이템을 찾아 맵핑을 시작합니다.
  const rootMenuItems = data.filter(item => item.submenuKey === 'ROOT')
  return rootMenuItems.map(item => createMenu(item.key))
}

const transformBookmarkData = data => {
  const iconMap = { ...Icons }

  return data.map(menu => ({
    ...menu,
    path: `${APP_PREFIX_PATH}/${menu.path}`,
    component: menu.path,
    showMenu: menu.displayFlag === 'Y' && menu.activeFlag === 'Y',
    breadcrumb: true,
    icon: iconMap[menu.icon],
  }))
}

export const selectMenuListByAuthorities = createAsyncThunk(
  'menu/select-menu-list-by-authorities',
  async (data, { rejectWithValue }) => {
    const response = await MenuService.selectMenuListByAuthorities()

    if (response) {
      return response
    } else {
      return rejectWithValue(response.message)
    }
  },
)

export const selectBookmarkListByUserId = createAsyncThunk(
  'menu/select-bookmark-list-by-user-id',
  async (data, { rejectWithValue }) => {
    const response = await MenuService.selectBookmarkListByUserId()

    if (response) {
      return response
    } else {
      return rejectWithValue(response.message)
    }
  },
)

const findHeaderKey = (data, key) => {
  for (let item of data) {
    if (item?.key === key) {
      return [item]
    }
  }
  return null
}

export const navigationSlice = createSlice({
  name: 'navigation',
  initialState,
  reducers: {
    clearReducers: state => {
      state.leftMenu = []
      state.navigationConfig = []
    },
    onLeftMenu: (state, action) => {
      state.leftMenu = findHeaderKey(state.navigationConfig, action.payload)
    },
    addBookmark: (state, action) => {
      MenuService.insertBookmark({ menuId: action.payload.key })
      state.bookmarkConfig = [...state.bookmarkConfig, action.payload]
    },
    removeBookmark: (state, action) => {
      MenuService.deleteBookmark({ menuId: action.payload })
      state.bookmarkConfig = state.bookmarkConfig.filter(item => item.key !== action.payload)
    },
  },
  extraReducers: builder => {
    builder
      .addCase(selectMenuListByAuthorities.pending, state => {
        state.loading = true
      })
      .addCase(selectMenuListByAuthorities.fulfilled, (state, action) => {
        state.loading = false
        state.menuList = action.payload
        state.navigationConfig = transformMenuData(action.payload)
      })
      .addCase(selectMenuListByAuthorities.rejected, (state, action) => {
        state.message = action.payload
        state.loading = false
      })
      .addCase(selectBookmarkListByUserId.pending, state => {
        state.loading = true
      })
      .addCase(selectBookmarkListByUserId.fulfilled, (state, action) => {
        state.loading = false
        state.bookmarkConfig = transformBookmarkData(action.payload)
      })
      .addCase(selectBookmarkListByUserId.rejected, (state, action) => {
        state.message = action.payload
        state.loading = false
      })
  },
})

export const { onLeftMenu, addBookmark, removeBookmark, clearReducers } = navigationSlice.actions
export default navigationSlice.reducer
